import React, { createContext, useContext } from "react"
import {
  graphql,
  useQueryLoader,
  usePreloadedQuery,
  PreloadedQuery,
  UseQueryLoaderLoadQueryOptions,
} from "react-relay"

import type {
  SmartMatchingContextQuery,
  SmartMatchingContextQuery$variables,
  SmartMatchingContextQuery$data,
} from "./__generated__/SmartMatchingContextQuery.graphql"

export type SmartMatchingData =
  SmartMatchingContextQuery$data["smartMatchSimilarPeople"]

export type SmartMatchingDataPerson =
  SmartMatchingContextQuery$data["smartMatchSimilarPeople"]["people"][0]

type SmartMatchingContextType = {
  queryRef: PreloadedQuery<
    SmartMatchingContextQuery,
    Record<string, unknown>
  > | null
  loadQuery: (
    variables: SmartMatchingContextQuery$variables,
    config?: UseQueryLoaderLoadQueryOptions,
  ) => void
}

const SmartMatchingContext = createContext<SmartMatchingContextType | null>(
  null,
)

export const SMART_MATCHING_QUERY = graphql`
  query SmartMatchingContextQuery(
    $personId: Int!
    $filterTargetPeopleIds: [Int!]
    $limitToPlaceholders: Boolean
  ) {
    smartMatchSimilarPeople(
      personId: $personId
      filterTargetPeopleIds: $filterTargetPeopleIds
      limitToPlaceholders: $limitToPlaceholders
    ) {
      people {
        personId
        totalScore
        matchedSkills {
          source {
            id
            level
          }
          target {
            id
            level
          }
          score
          distance
        }
        matchedCustomSelectOptions {
          source {
            id
            typeId
          }
          target {
            id
            typeId
          }
          score
          distance
        }
        scores {
          basicProfileMatchScore
          fieldScores {
            embeddingFieldId
            score
            distance
          }
          matchedSkillsScore
          matchedCustomSelectOptionsScore
        }
      }
      embeddingFields {
        id
        field
        model
        isIncluded
        customSelectTypeId
      }
    }
  }
`

export const SmartMatchingProvider = ({ children }) => {
  const [queryRef, loadQuery] =
    useQueryLoader<SmartMatchingContextQuery>(SMART_MATCHING_QUERY)
  const contextValue: SmartMatchingContextType = {
    queryRef,
    loadQuery,
  }

  return (
    <SmartMatchingContext.Provider value={contextValue}>
      {children}
    </SmartMatchingContext.Provider>
  )
}

export const useSmartMatchingData = () => {
  const context = useContext(SmartMatchingContext)
  if (!context) {
    throw new Error(
      "useSmartMatchingData must be used within SmartMatchingProvider",
    )
  }
  return context
}

const SmartMatchingQueryConsumer = ({
  queryRef,
  children,
}: {
  queryRef: PreloadedQuery<SmartMatchingContextQuery, Record<string, unknown>>
  children: (data: SmartMatchingContextQuery$data) => React.ReactNode
}) => {
  const data = usePreloadedQuery(SMART_MATCHING_QUERY, queryRef)
  return children(data)
}

// usePreloadedQuery is a hook and can't be used conditionally,
// so we need to ensure it's used in a child component.
// See https://relay.dev/docs/api-reference/use-query-loader/#usequeryloader
export const SmartMatchingDataRenderer = ({
  children,
  fallback,
}: {
  children: (data: SmartMatchingContextQuery$data | null) => React.ReactNode
  fallback?: React.ReactNode
}) => {
  const { queryRef } = useSmartMatchingData()

  if (!queryRef) {
    return children(null)
  }

  return (
    <React.Suspense fallback={fallback}>
      <SmartMatchingQueryConsumer queryRef={queryRef}>
        {children}
      </SmartMatchingQueryConsumer>
    </React.Suspense>
  )
}
